// COVER TEXT on screen (owner 2026-10-30): « I need to be able to use the text,
// make it bigger, make it smaller … control the width and the height of it ».
//
// Two pieces:
//   CoverTextContent — the band + the words, drawn in the element's box. The
//     SAME component draws the editor box and the read-only cover (the flow's
//     CoverSpreadPreview via CoverElementsView), so the two cannot disagree.
//   CoverTextBox — the team's editable box on a face: tap to select, drag to
//     move, tap again (or double-tap) to type (a plain-text textarea: any
//     phrase, several lines, pasted text stays plain), 8 handles that change the
//     BOX width and height independently (the letters keep their size and the
//     words re-wrap), and a rotation handle.
//
// PRINT PARITY (storiz-render-parity): the face layer is an inline-size query
// container, the font is `fontSize/4 cqi` (= fontSize px on a 400-px face, the
// print's textRasterPx with the face as the page), `line-height` and the band
// margin come from the same helpers the print reads (pdfTextLayout.
// textLineHeight, textBand.bandCss / bandRect), the words wrap at the box width
// (pre-wrap + break-word = pdfTextLayout.wrapTextLines) and a block taller than
// the box overflows it, centred — exactly what drawPageElements prints.
import { useCallback, useEffect, useLayoutEffect, useRef, type CSSProperties } from 'react';
import type { CoverElement } from '@/lib/coverElements';
import { bandCss } from '@/lib/textBand';
import { fontStackFor } from '@/lib/textBidi';
import { printOpticalSize, textLineHeight } from '@/lib/pdfTextLayout';

const GOLD = '#C9A24A';
const DANGER = '#DC2626';

/** The CSS that draws a cover text's letters — shared by the read-only view, the box and its textarea. */
export function coverTextStyle(el: CoverElement): CSSProperties {
  return {
    fontFamily: fontStackFor(el.fontFamily, el.text ?? ''),
    fontSize: `${(el.fontSize ?? 24) / 4}cqi`,
    fontVariationSettings: printOpticalSize(el.fontSize),
    fontWeight: el.fontWeight ?? '600',
    fontStyle: el.fontStyle ?? 'normal',
    textDecoration: el.textDecoration ?? 'none',
    color: el.fontColor ?? '#1F2937',
    textAlign: el.textAlign ?? 'center',
    lineHeight: textLineHeight(el),
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
    unicodeBidi: 'plaintext',
    ...(el.outlineColor && (el.outlineWidth ?? 0) > 0 ? {
      WebkitTextStroke: `${2 * (el.outlineWidth as number)}em ${el.outlineColor}`,
      paintOrder: 'stroke fill',
    } : {}),
  };
}

/** The band behind the words: the stored box grown by `bandPad` % of the FACE width (1cqi). */
function Band({ el }: { el: CoverElement }) {
  const bg = bandCss(el);
  if (!bg) return null;
  const pad = Math.max(0, Math.min(50, Number(el.bandPad ?? 0)) || 0);
  return (
    <div
      aria-hidden
      className="absolute pointer-events-none"
      style={{ inset: pad ? `${-pad}cqi` : 0, background: bg }}
    />
  );
}

/** The band and the words in the element's box (the box is the caller's). */
export function CoverTextContent({ el, spanRef, hidden }: {
  el: CoverElement;
  spanRef?: React.Ref<HTMLSpanElement>;
  /** While typing the textarea draws the words: keep the span for measuring only. */
  hidden?: boolean;
}) {
  return (
    <>
      <Band el={el} />
      <div
        className="absolute inset-0 flex pointer-events-none"
        style={{ alignItems: 'center', opacity: el.opacity ?? 1 }}
      >
        <span
          ref={spanRef}
          data-cover-text-words=""
          style={{ ...coverTextStyle(el), display: 'block', width: '100%', overflow: 'visible', visibility: hidden ? 'hidden' : undefined }}
        >
          {/* A trailing newline opens no line (pdfTextLayout.paragraphsOf); an empty text keeps one line. */}
          {(el.text ?? '').replace(/\n$/, '') || ' '}
        </span>
      </div>
    </>
  );
}

type Dir = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const HANDLES: Array<{ dir: Dir; l: number; t: number; cursor: string }> = [
  { dir: 'nw', l: 0, t: 0, cursor: 'nwse-resize' }, { dir: 'n', l: 50, t: 0, cursor: 'ns-resize' },
  { dir: 'ne', l: 100, t: 0, cursor: 'nesw-resize' }, { dir: 'e', l: 100, t: 50, cursor: 'ew-resize' },
  { dir: 'se', l: 100, t: 100, cursor: 'nwse-resize' }, { dir: 's', l: 50, t: 100, cursor: 'ns-resize' },
  { dir: 'sw', l: 0, t: 100, cursor: 'nesw-resize' }, { dir: 'w', l: 0, t: 50, cursor: 'ew-resize' },
];

/**
 * The new box when handle `dir` moves by (dx, dy) screen px, for a box turned
 * `rotationDeg` in a layer of (layerW × layerH) px. The opposite edge / corner
 * stays where it is on screen. Pure — exported for the tests.
 */
export function resizeTextBox(
  start: { x: number; y: number; width: number; height: number; rotation?: number },
  dir: Dir, dx: number, dy: number, layerW: number, layerH: number, minPx = 12,
): { x: number; y: number; width: number; height: number } {
  const th = ((start.rotation ?? 0) * Math.PI) / 180;
  const c = Math.cos(th), s = Math.sin(th);
  // The pointer's move in the box's own axes.
  const lx = dx * c + dy * s;
  const ly = -dx * s + dy * c;
  const w = (start.width / 100) * layerW, h = (start.height / 100) * layerH;
  let nw = w, nh = h;
  if (dir.includes('e')) nw = w + lx;
  if (dir.includes('w')) nw = w - lx;
  if (dir.includes('s')) nh = h + ly;
  if (dir.includes('n')) nh = h - ly;
  nw = Math.max(minPx, nw); nh = Math.max(minPx, nh);
  // The centre moves by half the growth, toward the dragged side, in box axes…
  const dcx = dir.includes('e') ? (nw - w) / 2 : dir.includes('w') ? -(nw - w) / 2 : 0;
  const dcy = dir.includes('s') ? (nh - h) / 2 : dir.includes('n') ? -(nh - h) / 2 : 0;
  // …turned back to the screen.
  const sx = dcx * c - dcy * s, sy = dcx * s + dcy * c;
  const cx = ((start.x + start.width / 2) / 100) * layerW + sx;
  const cy = ((start.y + start.height / 2) / 100) * layerH + sy;
  const width = (nw / layerW) * 100, height = (nh / layerH) * 100;
  return { x: (cx / layerW) * 100 - width / 2, y: (cy / layerH) * 100 - height / 2, width, height };
}

export interface CoverTextBoxProps {
  el: CoverElement;
  editable: boolean;
  selected: boolean;
  editing: boolean;
  /** What prints crosses the safe zone: red frame (drawn by the face layer). */
  outside?: boolean;
  onSelect: () => void;
  onStartEditing: () => void;
  onStopEditing: () => void;
  onUpdate: (u: Partial<CoverElement>) => void;
  /** The words' height in % of the face height (measured), for the overflow flag + safe zone. */
  onMeasure?: (heightPct: number) => void;
}

export function CoverTextBox({ el, editable, selected, editing, onSelect, onStartEditing, onStopEditing, onUpdate, onMeasure }: CoverTextBoxProps) {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const spanRef = useRef<HTMLSpanElement | null>(null);
  const areaRef = useRef<HTMLTextAreaElement | null>(null);
  const elRef = useRef(el);
  elRef.current = el;
  const locked = !!(el as { locked?: boolean }).locked;

  // ── Measure the words (overflow flag + safe zone) ─────────────────────
  const lastMeasure = useRef<number | null>(null);
  const measure = useCallback(() => {
    const span = spanRef.current;
    const layer = boxRef.current?.parentElement;
    if (!span || !layer || !onMeasure) return;
    const lh = layer.offsetHeight;
    if (lh <= 0) return;
    const pct = (span.offsetHeight / lh) * 100;
    if (lastMeasure.current == null || Math.abs(lastMeasure.current - pct) > 0.05) {
      lastMeasure.current = pct;
      onMeasure(pct);
    }
  }, [onMeasure]);
  useLayoutEffect(() => { measure(); });
  useEffect(() => {
    // Web fonts arrive after the first paint: measure again once they are in.
    let alive = true;
    document.fonts?.ready?.then(() => { if (alive) measure(); }).catch(() => {});
    const layer = boxRef.current?.parentElement;
    const ro = typeof ResizeObserver !== 'undefined' && layer ? new ResizeObserver(() => measure()) : null;
    if (ro && layer) ro.observe(layer);
    return () => { alive = false; ro?.disconnect(); };
  }, [measure, el.fontFamily]);

  // ── Typing ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!editing) return;
    const a = areaRef.current;
    if (!a) return;
    a.focus({ preventScroll: true });
    // A box still holding the placeholder is selected whole: typing replaces it.
    if ((el.text ?? '').trim() === 'Votre texte') a.select();
    else a.setSelectionRange(a.value.length, a.value.length);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing]);
  // The textarea is as tall as its lines (centred on the box like the words).
  useLayoutEffect(() => {
    const a = areaRef.current;
    if (!a) return;
    a.style.height = '0px';
    a.style.height = `${a.scrollHeight}px`;
  });

  // ── Move (and tap → select / type) ────────────────────────────────────
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!editable || editing) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    const wasSelected = selected;
    if (!selected) onSelect();
    if (locked) return;
    const layer = boxRef.current?.parentElement;
    const r = layer?.getBoundingClientRect();
    const lw = r?.width || 1, lh = r?.height || 1;
    const sx = e.clientX, sy = e.clientY;
    const start = { x: el.x, y: el.y };
    let moved = false;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* old browsers */ }
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - sx, dy = ev.clientY - sy;
      if (!moved && Math.hypot(dx, dy) < 4) return;
      moved = true;
      onUpdate({ x: start.x + (dx / lw) * 100, y: start.y + (dy / lh) * 100 });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      // A tap on a text that was already selected = type in it.
      if (!moved && wasSelected) onStartEditing();
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  // ── Resize: the BOX, width and height apart; the letters keep their size ──
  const onHandleDown = (dir: Dir) => (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const layer = boxRef.current?.parentElement;
    const r = layer?.getBoundingClientRect();
    if (!r) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* */ }
    const start = { ...elRef.current };
    const sx = e.clientX, sy = e.clientY;
    const onMove = (ev: PointerEvent) => {
      onUpdate(resizeTextBox(start, dir, ev.clientX - sx, ev.clientY - sy, r.width, r.height));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  // ── Rotate about the centre (snaps to 0 / 90 / 180 / 270 within 4°) ────
  const onRotateDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    e.preventDefault();
    const b = boxRef.current?.getBoundingClientRect();
    if (!b) return;
    try { (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId); } catch { /* */ }
    const cx = b.left + b.width / 2, cy = b.top + b.height / 2;
    const a0 = (Math.atan2(e.clientY - cy, e.clientX - cx) * 180) / Math.PI;
    const r0 = elRef.current.rotation ?? 0;
    const onMove = (ev: PointerEvent) => {
      const a = (Math.atan2(ev.clientY - cy, ev.clientX - cx) * 180) / Math.PI;
      let rot = r0 + (a - a0);
      rot = ((rot + 180) % 360 + 360) % 360 - 180;
      const snap = Math.round(rot / 90) * 90;
      if (Math.abs(rot - snap) < 4) rot = snap === 180 ? -180 : snap;
      onUpdate({ rotation: Math.round(rot * 10) / 10 });
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  const showChrome = editable && selected;
  // The editor's pages carry `locked` on an element (not in the print's type).
  return (
    <div
      ref={boxRef}
      data-element-id={el.id}
      data-cover-text={el.id}
      className="absolute"
      style={{
        left: `${el.x}%`, top: `${el.y}%`, width: `${el.width}%`, height: `${el.height}%`,
        transform: el.rotation ? `rotate(${el.rotation}deg)` : undefined,
        transformOrigin: 'center',
        cursor: editable ? (editing ? 'text' : selected ? 'move' : 'pointer') : undefined,
        touchAction: editable ? 'none' : undefined,
        pointerEvents: editable ? 'auto' : 'none',
        zIndex: selected ? 2 : undefined,
      }}
      onPointerDown={onPointerDown}
      onDoubleClick={(e) => { if (!editable) return; e.stopPropagation(); onSelect(); onStartEditing(); }}
    >
      <CoverTextContent el={el} spanRef={spanRef} hidden={editing} />
      {editing && (
        <div className="absolute inset-0 flex" style={{ alignItems: 'center' }}>
          <textarea
            ref={areaRef}
            data-cover-text-input=""
            value={el.text ?? ''}
            rows={1}
            spellCheck={false}
            onChange={(e) => onUpdate({ text: e.target.value })}
            onBlur={() => onStopEditing()}
            onPointerDown={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Escape' || (e.key === 'Enter' && (e.metaKey || e.ctrlKey))) { e.preventDefault(); e.currentTarget.blur(); }
            }}
            className="block w-full resize-none border-0 p-0 m-0 outline-none bg-transparent"
            style={{ ...coverTextStyle(el), overflow: 'hidden', opacity: el.opacity ?? 1, caretColor: el.fontColor ?? '#1F2937' }}
          />
        </div>
      )}
      {showChrome && (
        <>
          <div className="absolute pointer-events-none" style={{ inset: 0, border: `2px solid ${GOLD}`, boxShadow: `0 0 0 1px ${GOLD}30` }} />
          {!locked && !editing && HANDLES.map((h) => (
            <div
              key={h.dir}
              data-cover-text-handle={h.dir}
              className="absolute rounded-full"
              style={{
                left: `${h.l}%`, top: `${h.t}%`, transform: 'translate(-50%, -50%)',
                width: h.dir.length === 2 ? 12 : h.dir === 'n' || h.dir === 's' ? 22 : 7,
                height: h.dir.length === 2 ? 12 : h.dir === 'n' || h.dir === 's' ? 7 : 22,
                background: '#fff', border: `2px solid ${GOLD}`, cursor: h.cursor, touchAction: 'none',
                boxShadow: '0 1px 4px rgba(0,0,0,0.15)', zIndex: 3,
              }}
              onPointerDown={onHandleDown(h.dir)}
            />
          ))}
          {!locked && !editing && (
            <>
              <div className="absolute pointer-events-none" style={{ left: '50%', top: '100%', width: 1, height: 18, background: GOLD, transform: 'translateX(-50%)' }} />
              <div
                data-cover-text-rotate=""
                className="absolute rounded-full flex items-center justify-center"
                style={{
                  left: '50%', top: 'calc(100% + 18px)', width: 20, height: 20, transform: 'translate(-50%, -50%)',
                  background: '#fff', border: `2px solid ${GOLD}`, cursor: 'grab', touchAction: 'none', zIndex: 3,
                }}
                onPointerDown={onRotateDown}
                title="Rotation"
              >
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={GOLD} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 3v6h-6" />
                </svg>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}

/** A red frame on what prints when it leaves the safe zone (any element), with its hint. */
export function SafeZoneWarning({ box, label }: { box: { x: number; y: number; width: number; height: number; rotation?: number }; label?: boolean }) {
  return (
    <div
      data-cover-safe-warning=""
      className="absolute pointer-events-none"
      style={{
        left: `${box.x}%`, top: `${box.y}%`, width: `${box.width}%`, height: `${box.height}%`,
        transform: box.rotation ? `rotate(${box.rotation}deg)` : undefined, transformOrigin: 'center',
        outline: `2px dashed ${DANGER}`, outlineOffset: 1, zIndex: 4,
      }}
    >
      {label && (
        <span
          className="absolute left-1/2 whitespace-nowrap rounded-full px-1.5 py-0.5 text-[9px] font-semibold"
          style={{ top: -18, transform: 'translateX(-50%)', background: DANGER, color: '#fff' }}
        >
          sera coupé à l'impression
        </span>
      )}
    </div>
  );
}
